import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { InputStepper } from '@sunwebgroup/uds-ui/input-stepper'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Switch } from '@sunwebgroup/uds-ui/switch'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { sub } from 'date-fns'
import * as React from 'react'
import { useIntl } from '@/intl'
import {
  getOptionEquipments,
  getTotalPriceForEquipmentsSelected,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors'
import {
  CarListName,
  CarSheetContent,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/SheetViews'
import {
  editingCar,
  useCarRentalFilteringServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { editCar } from '@/scopes/checkout/domain/actions/services/carRentalFiltering'
import { createCarEvent, createEquipmentSelectionEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { parseDateForLocalFormatting } from '@/scopes/common/domain/utils/dates'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from './Equipments.module.css'

export function EquipmentsForm({ showEquipmentsForm, setShowEquipmentsForm, ...option }: CheckoutCarRentalServiceOption & { showEquipmentsForm: boolean, setShowEquipmentsForm: (value: boolean) => void }) {
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { endEditingCar, toggleEquipmentEnquiry } = useCarRentalFilteringServiceStore()
  const { label, value: carValue, id: carId, driverId } = option
  const driver = driverId ? service?.participants[driverId] : null
  const currentEditingCar = useCarRentalFilteringServiceStore(editingCar(carId))
  const [totalEquipmentPrice, setTotalEquipmentPrice] = React.useState(getTotalPriceForEquipmentsSelected(option) ?? 0)
  const { configurations } = service || {}

  const {
    bookedEquipmentsWithQuantity,
  } = currentEditingCar

  const availableEquipments = getOptionEquipments(option, bookedEquipmentsWithQuantity.filter(({ quantity }) => quantity > 0) ?? [])

  React.useEffect(() => {
    const totalPrice = bookedEquipmentsWithQuantity?.reduce((acc, { id, quantity }) => {
      const equipment = option.equipments.find(({ id: equipmentId }) => equipmentId === id)
      if (!equipment)
        return acc
      const price = (equipment.price ?? 0) * quantity
      return acc + price
    }, 0)
    setTotalEquipmentPrice(totalPrice ?? 0)
  }, [showEquipmentsForm, option, bookedEquipmentsWithQuantity, driverId, carId, service?.participants])
  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver })

  const handleEquipmentsQuantityChange = (value: string, quantity: number) => {
    toggleEquipmentEnquiry(carId, value, quantity)

    sendEvent(
      createEquipmentSelectionEvent({
        result: bookedEquipmentsWithQuantity.find(({ id }) => id === value) ? 'equipment_remove' : 'equipment_add',
        type: carValue,
        description: availableEquipments.find(equipment => equipment.id === value)?.text ?? '',
        element: 'input stepper',
        validated: true,
      }),
    )
  }

  const handleEquipmentsSwitchChange = (value: string, quantity: number) => {
    toggleEquipmentEnquiry(carId, value, quantity)
    sendEvent(
      createEquipmentSelectionEvent({
        result: bookedEquipmentsWithQuantity.find(({ id }) => id === value) ? 'equipment_remove' : 'equipment_add',
        type: carValue,
        description: availableEquipments.find(equipment => equipment.id === value)?.text ?? '',
        element: 'toggle',
        validated: true,
      }),
    )
  }

  const handleSaveValue
    = () => {
      if (!service)
        return
      if (!driver)
        return
      setServiceValue(editCar({
        value: carValue,
        id: carId,
        participantId: driverId ?? '',
        driverBirthDate: driver?.birthdate ? parseDateForLocalFormatting(driver.birthdate) : new Date(defaultDriverBirthdate),
        bookedEquipmentIds: bookedEquipmentsWithQuantity?.map(({ id }) => id) ?? [],
        bookedEquipmentsWithQuantity: bookedEquipmentsWithQuantity ?? [],
      })(service))

      endEditingCar(carId)
      setShowEquipmentsForm(false)

      sendEvent(
        createCarEvent({
          result: 'Car equipments selected',
          type: carValue,
          description: 'selectEquipments',
          element: 'button',
          validated: true,
        }),
      )
    }

  if (!service)
    return null
  if (!option)
    return null
  const lastEligibleId = availableEquipments?.filter(e => e.isElegible).at(-1)?.id
  const { equipments, maxNumberOfEquipments } = option
  const equipmentsLimit = maxNumberOfEquipments ?? equipments.length
  const totalEquipmentsSelected = availableEquipments?.reduce(
    (acc, item) => acc + item.quantitySelected,
    0,
  ) ?? 0

  return (
    <>
      <Sheet.Content>
        <CarSheetContent>
          <CarListName variant="display-h-regular">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_car_model' }, { carModel: label })}
            {' '}
          </CarListName>
          <Typography variant="body-m-regular-snug">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_select_extras_description' }, { max: option?.maxNumberOfEquipments })}
          </Typography>
          {availableEquipments && availableEquipments.length > 0 && (
            <div className={styles.formFieldContainer}>
              { availableEquipments?.map(({ id, text, selected, price, maxQuantity, quantitySelected, isElegible }) => {
                const isLastEligibleWithMaxReached
                  = isElegible
                    && id === lastEligibleId
                    && equipmentsLimit
                    && totalEquipmentsSelected >= equipmentsLimit

                let inputMaxQuantity = (maxQuantity && maxQuantity > equipmentsLimit) ? equipmentsLimit : maxQuantity

                if (totalEquipmentsSelected >= equipmentsLimit) {
                  inputMaxQuantity = quantitySelected
                }

                return (
                  <div key={id} className={styles.equipmentWrapper}>
                    <ListItem variant="outlined">
                      <div className={cn(styles.equipmentField, !isElegible && styles.equipmentFieldDisabled)}>
                        <Typography variant="body-m-regular-tight">
                          {text}
                          {' '}
                          {price ? `(${intl.formatCurrency(price)})` : ''}
                        </Typography>
                        <div className={styles.equipmentFieldTrailing}>
                          {maxQuantity && maxQuantity > 1 && (
                            <InputStepper
                              minimumValue={0}
                              maximumValue={inputMaxQuantity}
                              initialValue={quantitySelected}
                              disabled={!isElegible}
                              size="M"
                              id={id}
                              onChange={value => handleEquipmentsQuantityChange(id, value)}
                              decrementButtonAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_decrement_button_aria_label' }, { equipmentName: text })}
                              incrementButtonAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_increment_button_aria_label' }, { equipmentName: text })}
                              valueAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_value_aria_label' }, { equipmentName: text })}
                            />
                          )}
                          {(!maxQuantity || maxQuantity === 1) && (
                            <Switch
                              defaultChecked={selected}
                              value={id}
                              disabled={!isElegible}
                              onCheckedChange={value => handleEquipmentsSwitchChange(id, value ? 1 : 0)}
                              aria-label={text}
                            />
                          )}
                        </div>
                      </div>
                    </ListItem>
                    {isLastEligibleWithMaxReached && (
                      <Banner
                        color="neutral"
                        hasIcon
                        size="M"
                        text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_max_banner_text' })}
                        supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_max_banner_supporting_text' }, { limit: equipmentsLimit })}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}
          <ListItem variant="tinted">
            <div className={styles.totalEquipmentsPrice}>
              <div className={styles.totalEquipmentsPriceContent}>
                <Typography className={styles.totalEquipmentsPriceText} variant="body-l-bold-tight">
                  {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_total_price' })}
                </Typography>
                <Typography className={styles.totalEquipmentsPriceHintText} variant="body-s-regular-tight">
                  {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_total_price_hint_text' })}
                </Typography>
              </div>
              <Typography variant="body-l-bold-tight">{intl.formatCurrency(totalEquipmentPrice)}</Typography>
            </div>

          </ListItem>
        </CarSheetContent>
      </Sheet.Content>
      <Sheet.Footer>
        <Button color="primary" fullWidth onClick={handleSaveValue}>
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_save_button_text' })}
        </Button>
      </Sheet.Footer>
    </>
  )
}
